/**
 * Voice Assignment Suggestion API Client with React Query Hook
 *
 * Provides type-safe API client for AI-powered voice assignment suggestions using:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 * - Proper error handling and transformations
 */

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// STEP 1: Define Zod Schemas
// ============================================================================

/**
 * Schema for individual voice assignment (raw from API)
 * Includes enriched voice_name from backend
 */
const VoiceAssignmentRawSchema = z.object({
  personaIndex: z.number().int().min(0),
  personaName: z.string().min(1),
  voiceId: z.number().int(), // Voice ID as integer from backend
  voiceName: z.string().min(1),
  reasoning: z.string().min(10).max(500),
});

/**
 * Response schema for voice suggestions endpoint (raw from API)
 * Returns suggestions for episode personas (excluding narrator if present)
 */
const VoiceSuggestionsResponseRawSchema = z.object({
  episodeId: z.string().uuid(),
  languageCode: z.string(),
  hasNarrator: z.boolean(),
  narratorVoiceId: z.number().int().nullable(), // Voice ID as integer from backend
  narratorVoiceName: z.string().nullable(),
  suggestedVoiceAssignments: z.array(VoiceAssignmentRawSchema),
});

/**
 * Frontend-facing schema with voice IDs as strings (for HTML select compatibility)
 */
const VoiceAssignmentSchema = z.object({
  personaIndex: z.number().int().min(0),
  personaName: z.string().min(1),
  voiceId: z.string(), // Voice ID as string for frontend use
  voiceName: z.string().min(1),
  reasoning: z.string().min(10).max(500),
});

const VoiceSuggestionsResponseSchema = z.object({
  episodeId: z.string().uuid(),
  languageCode: z.string(),
  hasNarrator: z.boolean(),
  narratorVoiceId: z.string().nullable(), // Voice ID as string for frontend use
  narratorVoiceName: z.string().nullable(),
  suggestedVoiceAssignments: z.array(VoiceAssignmentSchema),
});

// Export TypeScript types from Zod schemas
export type VoiceAssignment = z.infer<typeof VoiceAssignmentSchema>;
export type VoiceSuggestionsResponse = z.infer<typeof VoiceSuggestionsResponseSchema>;

// ============================================================================
// STEP 2: Define Fetcher Functions
// ============================================================================

/**
 * Transform snake_case API response to camelCase frontend types
 */
function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }

  return transformed as T;
}

/**
 * Fetch voice assignment suggestions from backend
 *
 * Returns AI-powered voice suggestions for episode personas.
 * If episode has narrator, suggestions exclude narrator persona.
 *
 * @param episodeId - UUID of the episode
 * @param languageCode - Target language code (default: "en")
 * @returns Promise<VoiceSuggestionsResponse>
 */
const fetchVoiceAssignmentSuggestions = async (
  episodeId: string,
  _languageCode: string = 'en',
  localeCode?: string | null
): Promise<VoiceSuggestionsResponse> => {
  try {
    const params = localeCode ? `?locale_code=${encodeURIComponent(localeCode)}` : '';
    const data = await apiGet<unknown>(`/api/admin/episodes/${episodeId}/suggest-voices${params}`);

    const camelCased = transformSnakeToCamel<z.infer<typeof VoiceSuggestionsResponseRawSchema>>(
      data as Record<string, unknown>
    );

    // Parse raw response with integer voice IDs
    const rawResponse = VoiceSuggestionsResponseRawSchema.parse(camelCased);

    // Convert integer voice IDs to strings for frontend compatibility
    const response: VoiceSuggestionsResponse = {
      ...rawResponse,
      narratorVoiceId:
        rawResponse.narratorVoiceId !== null ? String(rawResponse.narratorVoiceId) : null,
      suggestedVoiceAssignments: rawResponse.suggestedVoiceAssignments.map((assignment) => ({
        ...assignment,
        voiceId: String(assignment.voiceId),
      })),
    };

    return VoiceSuggestionsResponseSchema.parse(response);
  } catch (error) {
    logger.error('Error fetching voice assignment suggestions:', error);
    throw error;
  }
};

// ============================================================================
// STEP 3: Define React Query Hooks
// ============================================================================

/**
 * Fetch AI-powered voice assignment suggestions for an episode
 *
 * Returns suggested voice assignments for episode personas.
 * Results are NOT cached (staleTime: 0) since suggestions may change
 * with backend LLM updates.
 *
 * @param episodeId - UUID of the episode (undefined to disable query)
 * @param languageCode - Target language code (default: "en")
 *
 * @example
 * ```tsx
 * const { data: suggestions, isLoading, error } = useSuggestVoices(episodeId, 'en');
 *
 * if (suggestions) {
 *   console.log('Has narrator:', suggestions.hasNarrator);
 *   suggestions.suggestedVoiceAssignments.forEach(assignment => {
 *     console.log(`${assignment.personaName} -> ${assignment.voiceName}`);
 *     console.log(`Reasoning: ${assignment.reasoning}`);
 *   });
 * }
 * ```
 */
export const useSuggestVoices = (
  episodeId: string | undefined,
  languageCode: string = 'en',
  localeCode?: string | null
) => {
  return useQuery({
    queryKey: ['voice-suggestions', episodeId, languageCode, localeCode],
    queryFn: () => fetchVoiceAssignmentSuggestions(episodeId!, languageCode, localeCode),
    enabled: false, // Manual trigger only - don't auto-fetch on language change
    staleTime: 0, // Don't cache suggestions (LLM may generate different results)
    gcTime: 0, // Don't keep in cache (ephemeral suggestions)
  });
};
